import { t } from "@lingui/macro"
import { observer } from "mobx-react"
import { useCallback, useEffect, useMemo } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"

import { ListPage } from "src/components/ListPage"
import { DataGridProTable } from "src/components/Table/DataGridPro"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import {
    getFilterModelForRepository,
    getSortModelForRepository,
} from "src/lib/data-grid-pro"
import { GlobalStore } from "src/store"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import {
    FilterModel,
    IAdvancedOperations,
    SortModel,
} from "src/types/data-grid-pro"
import { ConfirmModal } from "src/modals/confirm"
import { Repository } from "src/types/channel"
import { useInitializer } from "src/lib/initializer"
import { IPageFilterProps } from "src/components/PageFilter"
import { SegmentPickerButton } from "src/components/SegmentPickerButton"
import { removeLastIdSegment } from "src/helpers/removeLastIdSegment"
import { reportError } from "src/lib/report"
import { PollStore } from "src/views/polls/store"
import { useGetPollColumns } from "src/views/polls/hooks/useGetPollColumns"
import { IPollsItem } from "src/views/polls/types/polls-item"
import { PollsModal } from "src/modals/polls"
import { EModalMode } from "src/types/modal/modal"

const repository: Repository = "poll"

const View = observer(() => {
    const store = useStore(PollStore)
    const globalStore = useStore(GlobalStore)

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { poll_id } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const parentPath = removeLastIdSegment(location.pathname)

    const columns = useGetPollColumns()

    const advancedOperationsModel: IAdvancedOperations = useMemo(
        () => ({
            pagination: "server",
            filtering: "server",
            sorting: "server",
        }),
        [],
    )

    const advanceQuery = useMemo(() => {
        const filter = getFilterModelForRepository(
            repository,
            globalStore.session.dataGridFilterModel,
        )
        const sort = getSortModelForRepository(
            repository,
            globalStore.session.dataGridSortModel,
        )
        return { sort, filter }
    }, [
        globalStore.session.dataGridFilterModel,
        globalStore.session.dataGridSortModel,
    ])

    const initialized = useInitializer(async () => {
        await store.init(
            globalStore.session.accessGroup.id ?? DEFAULT_ACCESS_GROUP.id,
            advanceQuery,
        )
    }, [globalStore.session.accessGroup])

    // Open the modal for a particular poll from url
    useInitializer(async () => {
        if (poll_id !== undefined && !isNaN(Number(poll_id))) {
            await globalStore.modals.open(() => <PollsModal id={poll_id} />, {
                onClose: () => navigate(parentPath),
            })
        }
    }, [poll_id])

    useEffect(() => {
        return () => {
            store.dispose()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleDuplicatePollClick = useCallback(
        (item) => {
            if (item != null) {
                globalStore.modals.open(() => (
                    <PollsModal id={item.poll_id} mode={EModalMode.Copy} />
                ))
            } else {
                reportError(t`global.copy-feature-error`)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const header = useMemo(
        () => ({
            header: t`polls.header`,
            breadcrumbs: [t`polls.header`],
        }),
        [],
    )

    const handleSegmentChange = useCallback(
        (segments: number[]) => store.loadSegments(segments, advanceQuery),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [advanceQuery],
    )

    const filter = useMemo(
        (): IPageFilterProps => ({
            actions: (
                <SegmentPickerButton
                    value={store.segments}
                    onChange={handleSegmentChange}
                />
            ),
        }),
        [store.segments, handleSegmentChange],
    )

    const handleFilterChange = useCallback(
        async (model: FilterModel) => {
            const sort = getSortModelForRepository(
                repository,
                globalStore.session.dataGridSortModel,
            )
            await store.query({ sort, filter: model })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [globalStore.session.dataGridSortModel],
    )

    const handleSortChange = useCallback(
        async (model: SortModel) => {
            const filter = getFilterModelForRepository(
                repository,
                globalStore.session.dataGridFilterModel,
            )
            await store.query({ filter, sort: model })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [globalStore.session.dataGridFilterModel],
    )

    const handleEditItem = useCallback(
        (item: IPollsItem) => {
            globalStore.modals.open(() => (
                <PollsModal id={item.poll_id.toString()} />
            ))
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const handleDeleteItem = useCallback(
        (item: IPollsItem) => {
            globalStore.modals.open(
                () => (
                    <ConfirmModal
                        onConfirm={async (confirmed) => {
                            if (confirmed) {
                                await store.deleteItem(item.poll_id)
                            }
                        }}
                        title={t`polls.are-you-sure`}
                        content={`${t`polls.confirm-delete-item`} 
                            ${item.question}
                        `}
                    />
                ),
                {
                    variant: "slide-up-w600",
                },
            )
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const rowActionsRenderer = useCallback(
        (item: IPollsItem) => [
            {
                text: t`polls.edit-item`,
                onClick: () => handleEditItem(item),
            },
            {
                text: t`polls.duplicate-poll`,
                onClick: () => handleDuplicatePollClick(item),
                hidden: true, // we don't allow creating new polls from the polls list
            },
            {
                text: t`polls.delete-item`,
                onClick: () => handleDeleteItem(item),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    return (
        <ListPage header={header} filter={filter} loading={!initialized}>
            <DataGridProTable
                paginator={store.polls}
                data={store.polls.items}
                columns={columns}
                advancedOperations={advancedOperationsModel}
                onRowClickEvent={(params) => handleEditItem(params.row)}
                rowActionsRenderer={rowActionsRenderer}
                repository={repository}
                loading={!initialized}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
            />
        </ListPage>
    )
})

export const PollsView = () => (
    <StoreProvider Store={PollStore}>
        <View />
    </StoreProvider>
)
